import React from 'react';

// Hand-drawn, to-scale tyre visuals for the Tyre Size & Speedometer Error Calculator:
// SideView (original vs new overlaid on one ground line), CrossSection (front profile) and
// SpeedoGauge (indicated vs true needle).
//
// Two rendering rules apply to everything in this file:
//  1. Colours come from theme tokens via `style` (hsl(var(--token))) -- SVG presentation
//     attributes can't read CSS variables. Diagram-only tokens (--tyre-hl) live on the
//     :root in index.css.
//  2. Any SVG element with *dynamic children* (text content, mapped/conditional children)
//     is built with React.createElement (`h`) instead of JSX. The dev-only visual-edits
//     Babel plugin (plugins/visual-edits/babel-metadata-plugin.js) wraps every {expr} JSX
//     child of a lowercase element in an HTML <span>, which is invalid inside <svg> and
//     silently drops the content. Leaf elements whose only dynamic values are attributes
//     are safe as JSX.

const h = React.createElement;

const tok = (name, alpha) => (alpha === undefined ? `hsl(var(${name}))` : `hsl(var(${name}) / ${alpha})`);
const C = {
  fg: tok('--foreground'),
  muted: tok('--muted-foreground'),
  card: tok('--card'),
  newStroke: tok('--primary'),
  newFill: tok('--primary', 0.1),
  rubber: tok('--foreground', 0.16),
  tread: tok('--foreground', 0.42),
  rimFill: tok('--muted'),
  hl: tok('--tyre-hl'),
  hlFill: tok('--tyre-hl', 0.35),
};
const FONT = 'JetBrains Mono, ui-monospace, monospace';
const HALO = { paintOrder: 'stroke', stroke: C.card, strokeWidth: 3, strokeLinejoin: 'round' };

const f1 = (n) => (Math.round(n * 10) / 10).toFixed(1);
const n2 = (n) => Math.round(n * 100) / 100;

function txt(key, x, y, content, { anchor = 'start', size = 11, weight = 400, color = C.fg, rotate = null, halo = true } = {}) {
  return h('text', {
    key,
    x: n2(x),
    y: n2(y),
    textAnchor: anchor,
    fontSize: size,
    fontWeight: weight,
    fontFamily: FONT,
    transform: rotate === null ? undefined : `rotate(${rotate} ${n2(x)} ${n2(y)})`,
    style: { fill: color, ...(halo ? HALO : {}) },
  }, content);
}

// Dimension line with arrowheads at both ends (markers defined by defs()).
// `idp` is the per-SVG id prefix; `variant` picks the arrow colour ('new' | 'fg' | 'hl').
const VARIANT_COLOR = { new: C.newStroke, fg: C.fg, hl: C.hl };
function dimLine(key, idp, x1, y1, x2, y2, variant = 'new', width = 1.2) {
  const marker = `url(#${idp}-arrow-${variant})`;
  return (
    <line key={key} x1={n2(x1)} y1={n2(y1)} x2={n2(x2)} y2={n2(y2)} pointerEvents="none"
      markerStart={marker} markerEnd={marker} style={{ stroke: VARIANT_COLOR[variant], strokeWidth: width }} />
  );
}

// Per-SVG defs; ids are prefixed so the two diagrams on one page never share an id.
const arrowMarker = (id, color, orient) => (
  <marker key={id} id={id} viewBox="0 0 8 8" refX="4" refY="4" markerWidth="7" markerHeight="7" orient={orient} markerUnits="userSpaceOnUse">
    <path d="M0,0 L8,4 L0,8 Z" style={{ fill: color }} />
  </marker>
);
const defs = (idp) => h('defs', { key: 'defs' },
  arrowMarker(`${idp}-arrow-new`, C.newStroke, 'auto-start-reverse'),
  arrowMarker(`${idp}-arrow-fg`, C.fg, 'auto-start-reverse'),
  arrowMarker(`${idp}-arrow-hl`, C.hl, 'auto-start-reverse'),
  <filter key="glow" id={`${idp}-glow`} x="-20%" y="-20%" width="140%" height="140%">
    <feGaussianBlur stdDeviation="2.2" result="blur" />
    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
  </filter>,
);

// Annulus (ring) path between radii r1 < r2, centred on (cx, cy); evenodd fill.
const ringPath = (cx, cy, r1, r2) => {
  const circle = (r) => `M${n2(cx - r)},${n2(cy)} a${n2(r)},${n2(r)} 0 1,0 ${n2(2 * r)},0 a${n2(r)},${n2(r)} 0 1,0 ${n2(-2 * r)},0 Z`;
  return `${circle(r2)} ${circle(r1)}`;
};

const hlStyle = (idp) => ({ fill: C.hlFill, stroke: C.hl, strokeWidth: 2.2, strokeDasharray: '5 3', filter: `url(#${idp}-glow)` });

// Hover / tap target helper: pointer events on the drawn part itself. Uses className (not
// style) for the cursor, since a spread `style` would replace the element's fill/stroke.
const hit = (onPart, tyre, part) => (onPart ? {
  onMouseEnter: () => onPart('hover', tyre, part),
  onMouseLeave: () => onPart('leave', tyre, part),
  onClick: () => onPart('tap', tyre, part),
  className: 'cursor-pointer',
} : {});

const PART_CALLOUT = {
  width: (t) => `section width ${f1(t.widthMm)} mm`,
  aspect: (t) => `sidewall ${f1(t.sidewallMm)} mm (${t.aspectPct} % of width)`,
  construction: (t) => (t.zr ? 'ZR radial construction' : 'radial construction (R)'),
  rim: (t) => `rim ${t.rimIn}″ = ${f1(t.rimDiameterMm)} mm`,
  load: (t) => (t.loadIndex ? `load index ${t.loadIndex.index}: ≤ ${t.loadIndex.kg} kg per tyre` : 'no load index entered'),
  speed: (t) => {
    const s = t.speedSymbol;
    if (!s) return 'no speed symbol entered';
    return `speed symbol ${s.symbol ?? 'ZR'}: ${s.above ? '>' : '≤'} ${s.kmph} km/h`;
  },
};

const tyreName = (tyre) => (tyre === 'orig' ? 'Original' : 'New');

// ---------------------------------------------------------------------------
// Side view
// ---------------------------------------------------------------------------

export const SideView = ({ original, next, active, onPart, clearanceChangeMm }) => {
  const idp = 'tyre-side';
  const HL_STYLE = hlStyle(idp);
  const VW = 360;
  const VH = 304;
  const G = 256;
  const CX = 150;
  const s = 206 / Math.max(original.diameterMm, next.diameterMm);

  const frame = (t) => {
    const r = (t.diameterMm / 2) * s;
    const rr = t.rollingRadiusMm * s;
    const rRim = (t.rimDiameterMm / 2) * s;
    const tread = Math.min(14 * s, (r - rRim) * 0.3);
    return { r, rr, rRim, tread, cy: G - rr };
  };
  const fo = frame(original);
  const fn = frame(next);
  const flattened = next.rollingRadiusMm < next.diameterMm / 2 - 1e-6;

  const tyreGroup = (tyre, t, f) => {
    const isNew = tyre === 'new';
    const outline = isNew
      ? { stroke: C.newStroke, strokeWidth: 1.8 }
      : { stroke: C.muted, strokeWidth: 1.6, strokeDasharray: '6 4' };
    const items = [
      <path key="body" d={ringPath(CX, f.cy, f.rRim, f.r)} fillRule="evenodd"
        style={{ fill: isNew ? C.rubber : 'none', ...outline }} {...hit(isNew && onPart, tyre, 'aspect')} />,
      <path key="tread" d={ringPath(CX, f.cy, f.r - f.tread, f.r)} fillRule="evenodd"
        style={{ fill: isNew ? C.tread : 'none', stroke: 'none' }} {...hit(isNew && onPart, tyre, 'width')} />,
      <circle key="rim" cx={CX} cy={n2(f.cy)} r={n2(f.rRim)}
        style={{ fill: isNew ? C.rimFill : 'none', ...outline }} {...hit(isNew && onPart, tyre, 'rim')} />,
      <circle key="hub" cx={CX} cy={n2(f.cy)} r={3} style={{ fill: isNew ? C.newStroke : C.muted }} />,
    ];
    if (isNew) {
      for (let i = 0; i < 5; i += 1) {
        const a = (i * 2 * Math.PI) / 5 - Math.PI / 2;
        items.push(<line key={`spoke${i}`} x1={n2(CX + 8 * Math.cos(a))} y1={n2(f.cy + 8 * Math.sin(a))}
          x2={n2(CX + f.rRim * 0.82 * Math.cos(a))} y2={n2(f.cy + f.rRim * 0.82 * Math.sin(a))}
          style={{ stroke: C.muted, strokeWidth: 3, strokeLinecap: 'round', opacity: 0.55 }} pointerEvents="none" />);
      }
    }
    return h('g', { key: tyre, clipPath: 'url(#tyre-side-ground)' }, ...items);
  };

  const highlight = [];
  if (active) {
    const t = active.tyre === 'orig' ? original : next;
    const f = active.tyre === 'orig' ? fo : fn;
    const { part } = active;
    if (part === 'aspect') highlight.push(<path key="hl" d={ringPath(CX, f.cy, f.rRim, f.r - f.tread)} fillRule="evenodd" style={HL_STYLE} pointerEvents="none" />);
    if (part === 'width') highlight.push(<path key="hl" d={ringPath(CX, f.cy, f.r - f.tread, f.r)} fillRule="evenodd" style={HL_STYLE} pointerEvents="none" />);
    if (part === 'rim') highlight.push(<circle key="hl" cx={CX} cy={n2(f.cy)} r={n2(f.rRim)} style={HL_STYLE} pointerEvents="none" />);
    if (part === 'construction') {
      for (let i = 0; i < 40; i += 1) {
        const a = (i * 2 * Math.PI) / 40;
        highlight.push(<line key={`ply${i}`} x1={n2(CX + f.rRim * Math.cos(a))} y1={n2(f.cy + f.rRim * Math.sin(a))}
          x2={n2(CX + (f.r - f.tread) * Math.cos(a))} y2={n2(f.cy + (f.r - f.tread) * Math.sin(a))}
          style={{ stroke: C.hl, strokeWidth: 1.4 }} pointerEvents="none" />);
      }
    }
    if (part === 'load') {
      highlight.push(<line key="hl" x1={CX + 22} y1={n2(f.cy + 6)} x2={CX + 22} y2={G - 6} markerEnd={`url(#${idp}-arrow-hl)`} style={{ stroke: C.hl, strokeWidth: 3 }} pointerEvents="none" />);
    }
    if (part === 'speed') {
      const R = f.r + 8;
      const a1 = -150 * (Math.PI / 180);
      const a2 = -40 * (Math.PI / 180);
      const d = `M${n2(CX + R * Math.cos(a1))},${n2(f.cy + R * Math.sin(a1))} A${n2(R)},${n2(R)} 0 0,1 ${n2(CX + R * Math.cos(a2))},${n2(f.cy + R * Math.sin(a2))}`;
      highlight.push(<path key="hl" d={d} markerEnd={`url(#${idp}-arrow-hl)`} style={{ fill: 'none', stroke: C.hl, strokeWidth: 3 }} pointerEvents="none" />);
    }
    if (part === 'construction' || part === 'load' || part === 'speed') {
      highlight.push(<circle key="hl-outline" cx={CX} cy={n2(f.cy)} r={n2(f.r)} style={{ ...HL_STYLE, fill: 'none' }} pointerEvents="none" />);
    }
    highlight.push(txt('hl-callout', 8, VH - 6, `▶ ${tyreName(active.tyre)}: ${PART_CALLOUT[part](t)}`, { color: C.hl, weight: 700 }));
  }

  // Dimension lines + labels for the NEW tyre.
  const rMax = Math.max(fo.r, fn.r);
  const dimX = CX + rMax + 16;
  const topNew = fn.cy - fn.r;
  const botNew = fn.cy + fn.r;
  const dims = [
    dimLine('d-dia', idp, dimX, topNew, dimX, botNew),
    <line key="d-dia-ext1" x1={CX} y1={n2(topNew)} x2={dimX + 4} y2={n2(topNew)} style={{ stroke: C.newStroke, strokeWidth: 0.6, strokeDasharray: '2 2' }} />,
    txt('d-dia-t', dimX + 13, (topNew + botNew) / 2, `Ø ${f1(next.diameterMm)} mm`, { anchor: 'middle', rotate: -90, color: C.newStroke, weight: 700 }),
    dimLine('d-rim', idp, CX - fn.rRim, fn.cy, CX + fn.rRim, fn.cy, 'fg', 1),
    txt('d-rim-t1', CX, fn.cy - 18, `Rim ${next.rimIn}″`, { anchor: 'middle', size: 10 }),
    txt('d-rim-t2', CX, fn.cy - 6, `${f1(next.rimDiameterMm)} mm`, { anchor: 'middle', size: 10 }),
    dimLine('d-sw', idp, CX, fn.cy - fn.rRim, CX, topNew, 'fg', 1),
    txt('d-sw-t1', CX + 6, fn.cy - (fn.rRim + fn.r) / 2 - 2, 'Sidewall', { size: 10 }),
    txt('d-sw-t2', CX + 6, fn.cy - (fn.rRim + fn.r) / 2 + 10, `${f1(next.sidewallMm)} mm`, { size: 10 }),
    dimLine('d-rr', idp, CX, fn.cy + 4, CX, G, 'new', 1),
    txt('d-rr-t1', CX - 6, fn.cy + fn.rr / 2 + 2, flattened ? 'Rolling r' : 'Radius', { anchor: 'end', size: 10, color: C.newStroke }),
    txt('d-rr-t2', CX - 6, fn.cy + fn.rr / 2 + 14, `${f1(next.rollingRadiusMm)} mm`, { anchor: 'end', size: 10, color: C.newStroke }),
  ];
  if (flattened) {
    const half = Math.sqrt(Math.max(fn.r * fn.r - fn.rr * fn.rr, 0));
    dims.push(<line key="patch" x1={n2(CX - half)} y1={G} x2={n2(CX + half)} y2={G} style={{ stroke: C.newStroke, strokeWidth: 4 }} />);
  }

  const dClear = clearanceChangeMm;
  const clearText = `Ride height ${dClear >= 0 ? '+' : '−'}${f1(Math.abs(dClear))} mm (Δ diameter ÷ 2)`;

  const aria = `Side view to scale. Original tyre ${original.sizeLabel}, diameter ${f1(original.diameterMm)} mm, drawn dashed. `
    + `New tyre ${next.sizeLabel}, diameter ${f1(next.diameterMm)} mm, rim ${f1(next.rimDiameterMm)} mm, sidewall ${f1(next.sidewallMm)} mm, `
    + `${flattened ? 'rolling' : 'geometric'} radius ${f1(next.rollingRadiusMm)} mm. ${clearText}.`
    + (active ? ` Highlighted: ${tyreName(active.tyre)} ${PART_CALLOUT[active.part](active.tyre === 'orig' ? original : next)}.` : '');

  return h('svg', { viewBox: `0 0 ${VW} ${VH}`, className: 'w-full h-auto', role: 'img', 'aria-label': aria, 'data-testid': 'tyre-side-view' },
    defs(idp),
    <clipPath key="clip" id="tyre-side-ground"><rect x="0" y="0" width={VW} height={G} /></clipPath>,
    tyreGroup('new', next, fn),
    tyreGroup('orig', original, fo),
    <line key="ground" x1="6" y1={G} x2={VW - 6} y2={G} style={{ stroke: C.muted, strokeWidth: 1.5 }} />,
    <rect key="ground-fill" x="6" y={G} width={VW - 12} height="7" style={{ fill: tok('--muted-foreground', 0.15) }} />,
    ...dims,
    ...highlight,
    <line key="lg1" x1="8" y1="10" x2="30" y2="10" style={{ stroke: C.muted, strokeWidth: 1.6, strokeDasharray: '6 4' }} />,
    txt('lg1t', 36, 14, `Original ${original.sizeLabel} · Ø ${f1(original.diameterMm)}`, { size: 10, color: C.muted }),
    <line key="lg2" x1="8" y1="25" x2="30" y2="25" style={{ stroke: C.newStroke, strokeWidth: 2.4 }} />,
    txt('lg2t', 36, 29, `New ${next.sizeLabel} · Ø ${f1(next.diameterMm)}`, { size: 10, color: C.newStroke, weight: 700 }),
    txt('clear', 8, G + 22, clearText, { size: 10, color: C.muted }),
  );
};

// ---------------------------------------------------------------------------
// Cross-section (front) view
// ---------------------------------------------------------------------------

// Illustrative rim width as a fraction of section width (not a measured ETRTO rim).
const rimFraction = (aspect) => (aspect >= 60 ? 0.7 : aspect >= 45 ? 0.76 : 0.82);

const profilePath = (cx, base, W, Hh, Rw) => {
  const L = (x, y) => `${n2(x)},${n2(y)}`;
  return [
    `M${L(cx - Rw / 2, base)}`,
    `C${L(cx - Rw / 2 - 0.06 * W, base - 0.1 * Hh)} ${L(cx - W / 2, base - 0.22 * Hh)} ${L(cx - W / 2, base - 0.45 * Hh)}`,
    `C${L(cx - W / 2, base - 0.8 * Hh)} ${L(cx - 0.49 * W, base - Hh)} ${L(cx - 0.4 * W, base - Hh)}`,
    `L${L(cx + 0.4 * W, base - Hh)}`,
    `C${L(cx + 0.49 * W, base - Hh)} ${L(cx + W / 2, base - 0.8 * Hh)} ${L(cx + W / 2, base - 0.45 * Hh)}`,
    `C${L(cx + W / 2, base - 0.22 * Hh)} ${L(cx + Rw / 2 + 0.06 * W, base - 0.1 * Hh)} ${L(cx + Rw / 2, base)}`,
  ].join(' ');
};

const rimChannelPath = (cx, base, Rw) => {
  const L = (x, y) => `${n2(x)},${n2(y)}`;
  return [
    `M${L(cx - Rw / 2 - 3, base - 9)}`, `L${L(cx - Rw / 2 - 3, base + 3)}`, `L${L(cx - Rw / 2 + 5, base + 18)}`,
    `L${L(cx + Rw / 2 - 5, base + 18)}`, `L${L(cx + Rw / 2 + 3, base + 3)}`, `L${L(cx + Rw / 2 + 3, base - 9)}`,
  ].join(' ');
};

export const CrossSection = ({ original, next, active, onPart }) => {
  const idp = 'tyre-xs';
  const HL_STYLE = hlStyle(idp);
  const VW = 360;
  const VH = 252;
  const B = 190;
  const CX = 160;
  const sc = Math.min(200 / Math.max(original.widthMm, next.widthMm), 118 / Math.max(original.sidewallMm, next.sidewallMm));
  const geom = (t) => {
    const W = t.widthMm * sc;
    return { W, Hh: t.sidewallMm * sc, Rw: W * rimFraction(t.aspectPct) };
  };
  const go = geom(original);
  const gn = geom(next);
  const maxW = Math.max(go.W, gn.W);
  const maxH = Math.max(go.Hh, gn.Hh);

  const items = [
    <path key="new-body" d={`${profilePath(CX, B, gn.W, gn.Hh, gn.Rw)} Z`} style={{ fill: C.rubber, stroke: C.newStroke, strokeWidth: 1.8 }} {...hit(onPart, 'new', 'aspect')} />,
    <rect key="new-tread" x={n2(CX - 0.4 * gn.W)} y={n2(B - gn.Hh)} width={n2(0.8 * gn.W)} height={n2(Math.min(9 * sc, gn.Hh * 0.2))}
      style={{ fill: C.tread }} {...hit(onPart, 'new', 'width')} />,
    <path key="new-rim" d={rimChannelPath(CX, B, gn.Rw)} style={{ fill: 'none', stroke: C.fg, strokeWidth: 2.2, strokeLinejoin: 'round' }} {...hit(onPart, 'new', 'rim')} />,
    <path key="orig-body" d={`${profilePath(CX, B, go.W, go.Hh, go.Rw)} Z`} style={{ fill: 'none', stroke: C.muted, strokeWidth: 1.6, strokeDasharray: '6 4' }} pointerEvents="none" />,
    <path key="orig-rim" d={rimChannelPath(CX, B, go.Rw)} style={{ fill: 'none', stroke: C.muted, strokeWidth: 1.4, strokeDasharray: '4 3' }} pointerEvents="none" />,
  ];

  const highlight = [];
  if (active) {
    const g = active.tyre === 'orig' ? go : gn;
    const t = active.tyre === 'orig' ? original : next;
    const { part } = active;
    if (part === 'width') {
      highlight.push(<line key="hl-w" x1={n2(CX - g.W / 2)} y1={n2(B - 0.45 * g.Hh)} x2={n2(CX + g.W / 2)} y2={n2(B - 0.45 * g.Hh)}
        markerStart={`url(#${idp}-arrow-hl)`} markerEnd={`url(#${idp}-arrow-hl)`} style={{ stroke: C.hl, strokeWidth: 2.4 }} pointerEvents="none" />);
    }
    if (part === 'aspect') {
      highlight.push(<line key="hl-h" x1={CX} y1={B} x2={CX} y2={n2(B - g.Hh)} markerStart={`url(#${idp}-arrow-hl)`} markerEnd={`url(#${idp}-arrow-hl)`}
        style={{ stroke: C.hl, strokeWidth: 2.4 }} pointerEvents="none" />);
    }
    if (part === 'rim') {
      highlight.push(<path key="hl" d={rimChannelPath(CX, B, g.Rw)} style={{ ...HL_STYLE, fill: 'none', strokeWidth: 3.2 }} pointerEvents="none" />);
    } else if (part === 'construction') {
      highlight.push(<path key="hl" d={profilePath(CX, B - 3, g.W * 0.86, g.Hh * 0.88, g.Rw * 0.92)} style={{ fill: 'none', stroke: C.hl, strokeWidth: 2.4, strokeDasharray: '5 3' }} pointerEvents="none" />);
    } else {
      highlight.push(<path key="hl" d={`${profilePath(CX, B, g.W, g.Hh, g.Rw)} Z`} style={HL_STYLE} pointerEvents="none" />);
    }
    highlight.push(txt('hl-callout', 8, VH - 6, `▶ ${tyreName(active.tyre)}: ${PART_CALLOUT[part](t)}`, { color: C.hl, weight: 700 }));
  }

  const wy = B - maxH - 14;
  const hx = CX + maxW / 2 + 18;
  const dims = [
    dimLine('d-w', idp, CX - gn.W / 2, wy, CX + gn.W / 2, wy),
    <line key="d-w-e1" x1={n2(CX - gn.W / 2)} y1={n2(wy - 4)} x2={n2(CX - gn.W / 2)} y2={n2(B - 0.45 * gn.Hh)} style={{ stroke: C.newStroke, strokeWidth: 0.6, strokeDasharray: '2 2' }} />,
    <line key="d-w-e2" x1={n2(CX + gn.W / 2)} y1={n2(wy - 4)} x2={n2(CX + gn.W / 2)} y2={n2(B - 0.45 * gn.Hh)} style={{ stroke: C.newStroke, strokeWidth: 0.6, strokeDasharray: '2 2' }} />,
    txt('d-w-t', CX, wy - 6, `Width ${f1(next.widthMm)} mm`, { anchor: 'middle', color: C.newStroke, weight: 700 }),
    dimLine('d-h', idp, hx, B, hx, B - gn.Hh),
    <line key="d-h-e" x1={n2(CX + 0.4 * gn.W)} y1={n2(B - gn.Hh)} x2={hx + 4} y2={n2(B - gn.Hh)} style={{ stroke: C.newStroke, strokeWidth: 0.6, strokeDasharray: '2 2' }} />,
    txt('d-h-t', hx + 13, B - gn.Hh / 2, `Sidewall ${f1(next.sidewallMm)}`, { anchor: 'middle', rotate: -90, color: C.newStroke, weight: 700, size: 10 }),
    txt('d-rim-t', CX, B + 34, `Rim seat: ${next.rimIn}″ (${f1(next.rimDiameterMm)} mm dia.)`, { anchor: 'middle', size: 10 }),
  ];

  const aria = `Cross-section. New tyre ${next.sizeLabel}: section width ${f1(next.widthMm)} mm, sidewall height ${f1(next.sidewallMm)} mm, `
    + `rim ${next.rimIn} inch. Original ${original.sizeLabel} dashed: width ${f1(original.widthMm)} mm, sidewall ${f1(original.sidewallMm)} mm.`
    + (active ? ` Highlighted: ${tyreName(active.tyre)} ${PART_CALLOUT[active.part](active.tyre === 'orig' ? original : next)}.` : '');

  return h('svg', { viewBox: `0 0 ${VW} ${VH}`, className: 'w-full h-auto', role: 'img', 'aria-label': aria, 'data-testid': 'tyre-cross-section' },
    defs(idp),
    ...items,
    ...dims,
    ...highlight,
    <line key="lg1" x1="8" y1="10" x2="30" y2="10" style={{ stroke: C.muted, strokeWidth: 1.6, strokeDasharray: '6 4' }} />,
    txt('lg1t', 36, 14, `Original ${original.widthMm}/${original.aspectPct}`, { size: 10, color: C.muted }),
    <line key="lg2" x1="8" y1="25" x2="30" y2="25" style={{ stroke: C.newStroke, strokeWidth: 2.4 }} />,
    txt('lg2t', 36, 29, `New ${next.widthMm}/${next.aspectPct}`, { size: 10, color: C.newStroke, weight: 700 }),
  );
};

// ---------------------------------------------------------------------------
// Speedometer gauge
// ---------------------------------------------------------------------------

export const SpeedoGauge = ({ indicatedKmph, trueKmph, unit }) => {
  const toUnit = (v) => (unit === 'mph' ? v / 1.609344 : v);
  const unitLabel = unit === 'mph' ? 'mph' : 'km/h';
  const max = unit === 'mph' ? 160 : 260;
  const minorStep = unit === 'mph' ? 10 : 10;
  const labelStep = unit === 'mph' ? 20 : 40;
  const VW = 280;
  const VH = 210;
  const cx = 140;
  const cy = 142;
  const R = 112;
  const pt = (v, r) => {
    const th = Math.PI - (Math.min(Math.max(v, 0), max) / max) * Math.PI;
    return [cx + r * Math.cos(th), cy - r * Math.sin(th)];
  };
  const ind = toUnit(indicatedKmph);
  const tru = toUnit(trueKmph);

  const ticks = [];
  for (let v = 0; v <= max; v += minorStep) {
    const major = v % labelStep === 0;
    const [x1, y1] = pt(v, R);
    const [x2, y2] = pt(v, R - (major ? 12 : 6));
    ticks.push(<line key={`t${v}`} x1={n2(x1)} y1={n2(y1)} x2={n2(x2)} y2={n2(y2)} style={{ stroke: C.muted, strokeWidth: major ? 2 : 1 }} />);
    if (major) {
      const [tx, ty] = pt(v, R - 26);
      ticks.push(txt(`l${v}`, tx, ty + 4, String(v), { anchor: 'middle', size: 10, color: C.muted, halo: false }));
    }
  }
  const [ix, iy] = pt(ind, R - 20);
  const [tx, ty] = pt(tru, R - 6);

  // Difference strip: true - indicated on a symmetric scale.
  const diff = tru - ind;
  const span = Math.max(2, Math.ceil(Math.abs(diff) * 1.6));
  const sx0 = 40;
  const sx1 = 240;
  const sy = 190;
  const sx = (d) => (sx0 + sx1) / 2 + (d / span) * ((sx1 - sx0) / 2);

  const aria = `Speedometer at ${f1(ind)} ${unitLabel} indicated; true speed ${f1(tru)} ${unitLabel}; `
    + `${Math.abs(diff) < 0.05 ? 'no difference' : `true speed is ${f1(Math.abs(diff))} ${unitLabel} ${diff > 0 ? 'above' : 'below'} the reading`}.`;

  return h('svg', { viewBox: `0 0 ${VW} ${VH}`, className: 'w-full h-auto max-w-sm mx-auto', role: 'img', 'aria-label': aria, 'data-testid': 'tyre-speedo-gauge' },
    <path key="arc" d={`M${cx - R},${cy} A${R},${R} 0 0,1 ${cx + R},${cy}`} style={{ fill: 'none', stroke: C.muted, strokeWidth: 2 }} />,
    ...ticks,
    <line key="n-ind" x1={cx} y1={cy} x2={n2(ix)} y2={n2(iy)} style={{ stroke: C.muted, strokeWidth: 5, strokeLinecap: 'round', opacity: 0.75 }} />,
    <line key="n-true" x1={cx} y1={cy} x2={n2(tx)} y2={n2(ty)} style={{ stroke: C.newStroke, strokeWidth: 2.2, strokeLinecap: 'round', strokeDasharray: '7 3' }} />,
    <circle key="hub" cx={cx} cy={cy} r="6" style={{ fill: C.fg }} />,
    txt('unit', cx, cy - 34, unitLabel, { anchor: 'middle', size: 10, color: C.muted, halo: false }),
    txt('read', cx, cy + 24, `shows ${f1(ind)}`, { anchor: 'middle', size: 12, weight: 700 }),
    <line key="s-axis" x1={sx0} y1={sy} x2={sx1} y2={sy} style={{ stroke: C.muted, strokeWidth: 1.5 }} />,
    <line key="s-zero" x1={(sx0 + sx1) / 2} y1={sy - 7} x2={(sx0 + sx1) / 2} y2={sy + 7} style={{ stroke: C.muted, strokeWidth: 1.5 }} />,
    txt('s-min', sx0, sy + 16, `−${span}`, { anchor: 'middle', size: 9, color: C.muted, halo: false }),
    txt('s-max', sx1, sy + 16, `+${span}`, { anchor: 'middle', size: 9, color: C.muted, halo: false }),
    txt('s-lbl', sx0 - 6, sy + 4, 'Δ', { anchor: 'end', size: 11, color: C.muted, halo: false }),
    <path key="s-mark" d={`M${n2(sx(diff))},${sy - 3} l-6,-10 h12 Z`} style={{ fill: C.newStroke }} />,
  );
};
